import { cn } from '../lib/utils';

export const ScoreDisplay = ({ score, size = 'large', showLabel = true, className }) => {
  const getScoreColor = (score) => {
    if (score >= 85) return 'from-emerald-500 to-emerald-600';
    if (score >= 70) return 'from-lime-500 to-lime-600';
    if (score >= 55) return 'from-amber-500 to-amber-600';
    return 'from-red-500 to-red-600';
  };

  const getScoreStatus = (score) => {
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 55) return 'Average';
    return 'Needs Improvement';
  };

  const getBgColor = (score) => {
    if (score >= 85) return 'bg-emerald-500';
    if (score >= 70) return 'bg-lime-500';
    if (score >= 55) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const sizeClasses = {
    large: 'w-36 h-36',
    medium: 'w-24 h-24',
    small: 'w-16 h-16'
  };

  const fontSizes = {
    large: 'text-4xl',
    medium: 'text-2xl',
    small: 'text-lg'
  };

  const ringWidth = {
    large: 8,
    medium: 6,
    small: 4
  };

  const percentage = Math.min(100, Math.max(0, score));
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      <div className={cn('relative', sizeClasses[size])}>
        {/* Background ring */}
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="currentColor"
            strokeWidth={ringWidth[size]}
            className="text-slate-200"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="url(#scoreGradient)"
            strokeWidth={ringWidth[size]}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
          <defs>
            <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" className={cn('stop-color-start', score >= 85 ? 'stop-emerald-400' : score >= 70 ? 'stop-lime-400' : score >= 55 ? 'stop-amber-400' : 'stop-red-400')} stopColor={score >= 85 ? '#34d399' : score >= 70 ? '#a3e635' : score >= 55 ? '#fbbf24' : '#f87171'} />
              <stop offset="100%" className={cn('stop-color-end', score >= 85 ? 'stop-emerald-600' : score >= 70 ? 'stop-lime-600' : score >= 55 ? 'stop-amber-600' : 'stop-red-600')} stopColor={score >= 85 ? '#059669' : score >= 70 ? '#65a30d' : score >= 55 ? '#d97706' : '#dc2626'} />
            </linearGradient>
          </defs>
        </svg>
        
        {/* Score value */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn(
            'font-bold font-mono',
            fontSizes[size],
            score >= 85 ? 'text-emerald-600' : score >= 70 ? 'text-lime-600' : score >= 55 ? 'text-amber-600' : 'text-red-600'
          )}>
            {Math.round(score)}
          </span>
        </div>
      </div>
      
      {showLabel && (
        <span className={cn(
          'px-3 py-1 rounded-full text-xs font-semibold text-white',
          getBgColor(score)
        )}>
          {getScoreStatus(score)}
        </span>
      )}
    </div>
  );
};

export const ScoreBar = ({ score, label, className }) => {
  const getBarColor = (score) => {
    if (score >= 85) return 'bg-emerald-500';
    if (score >= 70) return 'bg-lime-500';
    if (score >= 55) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">{label}</span>
          <span className="font-mono font-semibold">{Math.round(score)}</span>
        </div>
      )}
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className={cn('h-full rounded-full transition-all duration-500', getBarColor(score))}
          style={{ width: `${Math.min(100, score)}%` }}
        />
      </div>
    </div>
  );
};
